import type { AddLiabilityFormValues } from "@/components/Forms/AddEditLiabilityDialog.tsx";
import { useFetchPeopleForAutocomplete } from "@/queries/useFetchPeopleForAutocomplete.ts";
import { Autocomplete, CircularProgress, TextField, Typography } from "@mui/material";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

export type Option = {
    id: string | null;
    name: string | null;
};

type Props = {
    form: UseFormReturn<AddLiabilityFormValues>;
};

const LiabilityAdjusterAutocomplete = ({ form }: Props) => {
    const [searchString, setSearchString] = useState("");
    const [selectedOption, setSelectedOption] = useState<Option | null>(null);
    const adjusterIdFromForm = form.watch("idAdjuster");
    const adjusterError = form.formState.errors.idAdjuster;
    const isQueryEnabled =
        searchString.length >= 2 ||
        (adjusterIdFromForm !== null && adjusterIdFromForm !== undefined);

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchPeopleForAutocomplete({
        searchString: searchString,
        fieldIdValue: adjusterIdFromForm ?? null,
        isQueryEnabled: isQueryEnabled,
        subQueryKey: "liability-adjuster-autocomplete",
    });

    const options: Option[] = useMemo(() => {
        if (data) {
            return data.map((person) => ({
                id: person.idUUID,
                name: person.fullName ?? "",
            }));
        }
        return [];
    }, [data]);

    // Update selected option when form value changes (for edit mode)
    useEffect(() => {
        if (adjusterIdFromForm && options.length > 0) {
            const foundOption = options.find((option) => option.id === adjusterIdFromForm);
            if (foundOption && foundOption.id !== selectedOption?.id) {
                setSelectedOption(foundOption);
            }
        } else if (!adjusterIdFromForm) {
            setSelectedOption(null);
        }
    }, [adjusterIdFromForm, options, selectedOption?.id]);

    const handleChange = (_event: React.SyntheticEvent, newValue: Option | null) => {
        setSelectedOption(newValue);

        form.setValue("idAdjuster", newValue?.id || "");

        if (newValue && data) {
            const selectedAdjuster = data.find((item) => item.idUUID === newValue.id);

            if (selectedAdjuster) {
                form.setValue("namePrefix", selectedAdjuster.namePrefix || "");
                form.setValue("nameFirst", selectedAdjuster.nameFirst || "");
                form.setValue("nameI", selectedAdjuster.nameI || "");
                form.setValue("nameLast", selectedAdjuster.nameLast || "");
                form.setValue("nameSuffix", selectedAdjuster.nameSuffix || "");
                form.setValue("nameAKA", selectedAdjuster.nameAKA || "");

                // Set address fields
                form.setValue("address", selectedAdjuster.address || "");
                form.setValue("address2", selectedAdjuster.address2 || "");
                form.setValue("city", selectedAdjuster.city || "");
                form.setValue("state", selectedAdjuster.state || "");
                form.setValue("zip", selectedAdjuster.zip || "");

                // Set contact fields - find primary phone, fax and email
                const primaryEmail = selectedAdjuster.allEmailAddresses?.find(
                    (email) => email.primary === "Yes",
                );
                const primaryPhone = selectedAdjuster.allPhoneNumbers?.find(
                    (phone) => phone.primary === "Yes",
                );
                const faxNumber = selectedAdjuster.allPhoneNumbers?.find(
                    (phone) => phone.phoneType === "Fax",
                );

                form.setValue("phoneNumber", primaryPhone?.phoneNumber || "");
                form.setValue("phoneExtension", primaryPhone?.extension || "");
                form.setValue("faxNumber", faxNumber?.phoneNumber || "");
                form.setValue("email", primaryEmail?.emailAddress || "");
            }
        } else {
            const adjusterFields = [
                "namePrefix",
                "nameFirst",
                "nameI",
                "nameLast",
                "nameSuffix",
                "nameAKA",
                "phoneNumber",
                "phoneExtension",
                "email",
                "address",
                "address2",
                "city",
                "state",
                "zip",
            ];

            adjusterFields.forEach((field) => {
                form.setValue(field as keyof AddLiabilityFormValues, "");
            });
        }
    };

    const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
        if (newInputValue.length >= 2) {
            setSearchString(newInputValue);
        }
    };

    return (
        <>
            {isError && <Typography variant={"h5"}>Error loading adjusters</Typography>}
            <Autocomplete
                value={selectedOption}
                onChange={handleChange}
                onInputChange={handleInputChange}
                options={options}
                loading={isLoading || isRefetching}
                getOptionLabel={(option: Option) => option.name || ""}
                getOptionKey={(option: Option) => option.id || ""}
                isOptionEqualToValue={(option: Option, value: Option) => option.id === value.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Adjuster"
                        error={!!adjusterError}
                        helperText={adjusterError?.message || ""}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading || isRefetching ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                sx={{
                    "& .MuiAutocomplete-inputRoot": {
                        padding: "4px!important",
                    },
                    "& .MuiAutocomplete-input": {
                        paddingTop: "4px!important",
                        paddingBottom: "4px!important",
                    },
                }}
            />
        </>
    );
};

export default LiabilityAdjusterAutocomplete;
